import LegalLayout, { Section } from "@/components/LegalLayout";

const LAST_UPDATED = "27 February 2026";
const PREFIX = "aup";

export default function AcceptableUsePage() {
  return (
    <LegalLayout
      title="Acceptable Use Policy"
      canonical="https://transportanything247.com/aup"
      description="Rules for using the Transport Anything 247 marketplace and apps — what is allowed and what isn't."
      lastUpdated={LAST_UPDATED}
      testidPrefix={PREFIX}
    >
      <Section id="overview" title="1. Overview" testidPrefix={PREFIX}>
        <p>
          This Acceptable Use Policy ("AUP") sets out the rules for using the transportanything247.com
          website, the Transport Anything 247 customer mobile app and the Transport 247 Driver mobile
          app (together, "the Service"). It supplements our{" "}
          <a href="/terms" className="text-orange-600 underline">Terms of Service</a> and{" "}
          <a href="/privacy" className="text-orange-600 underline">Privacy Policy</a>.
        </p>
        <p>
          Breaches of this AUP may result in immediate suspension or termination of your account and,
          where applicable, reporting to law-enforcement or regulatory authorities.
        </p>
      </Section>

      <Section id="prohibited-items" title="2. Prohibited items" testidPrefix={PREFIX}>
        <p>You must not list, request transport for, or carry any of the following items via the Service:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Controlled drugs and psychotropic substances (other than pharmacy-shipped prescriptions handled by licensed couriers).</li>
          <li>Firearms, ammunition, explosives, replicas thereof, and parts intended for assembly into firearms.</li>
          <li>Counterfeit goods, items with forged or altered identification, stolen property.</li>
          <li>Live animals (other than support animals accompanying a person; the Service does not currently support pet transport).</li>
          <li>Human remains, organs or other body tissue.</li>
          <li>Cash, bearer-negotiable instruments, financial securities — except via professionally insured cash-in-transit carriers using their own platforms.</li>
          <li>Items prohibited by UK law from carriage on the relevant vehicle class.</li>
        </ul>
      </Section>

      <Section id="hazardous" title="3. Hazardous goods" testidPrefix={PREFIX}>
        <p>
          Some categories on the Service (e.g. fuel, batteries above certain capacities, paints,
          aerosols, chemicals) are flagged as <strong>hazardous</strong>. You may only book or accept
          these jobs if:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>you explicitly select the relevant hazardous category at booking;</li>
          <li>(drivers) you hold the relevant ADR / dangerous-goods qualification and your insurance covers carriage of the specific class;</li>
          <li>(both) you comply with all UK ADR labelling and packaging requirements.</li>
        </ul>
      </Section>

      <Section id="account-rules" title="4. Account and identity rules" testidPrefix={PREFIX}>
        <ul className="list-disc pl-5 space-y-1">
          <li>One account per person. Do not create or operate multiple accounts to evade suspensions or platform controls.</li>
          <li>Do not share account credentials. Drivers in particular must not let another person drive jobs under their account — insurance, dispatch fairness and customer safety all depend on identity matching.</li>
          <li>Do not impersonate any other person, business or organisation.</li>
          <li>Provide accurate, current information when registering and update it promptly if it changes.</li>
        </ul>
      </Section>

      <Section id="conduct" title="5. Conduct with other users" testidPrefix={PREFIX}>
        <p>
          The Service includes in-app chat between customers, drivers and our support team. When
          using chat or any communication channel on the Service:
        </p>
        <ul className="list-disc pl-5 space-y-1">
          <li>Treat the other party respectfully. No harassment, abuse, threats or hate speech.</li>
          <li>No spam, unsolicited marketing, or referral-link blasts.</li>
          <li>No sharing of payment or contact details with intent to circumvent the platform's payment + dispute systems ("off-platform" deals are not allowed because they bypass driver insurance verification and customer protection).</li>
          <li>Do not transmit malicious code, links, or attempts to phish other users.</li>
        </ul>
      </Section>

      <Section id="platform-integrity" title="6. Platform integrity" testidPrefix={PREFIX}>
        <p>You must not:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>scrape, crawl or systematically download content from the Service (we publish a public sitemap and welcome accessibility-friendly indexing);</li>
          <li>interfere with or disrupt the Service or its servers (DDoS, load testing without permission, exploiting bugs for advantage);</li>
          <li>reverse engineer or attempt to derive source code from the apps, except as permitted by mandatory law;</li>
          <li>use bots, automated scripts or unofficial clients to interact with the Service;</li>
          <li>artificially inflate jobs completed, ratings, efficiency metrics or any other platform signals.</li>
        </ul>
      </Section>

      <Section id="driver-specific" title="7. Driver-specific rules" testidPrefix={PREFIX}>
        <p>In addition to the above, approved drivers must:</p>
        <ul className="list-disc pl-5 space-y-1">
          <li>maintain a valid UK driving licence and current goods-carriage insurance for every vehicle used on the platform;</li>
          <li>operate only roadworthy vehicles and comply with all driver-hours / tachograph regulations applicable to your vehicle class;</li>
          <li>capture honest proof-of-delivery photos at the actual delivery point — manipulated or staged PODs are a serious breach of this AUP;</li>
          <li>not solicit cash payments off-platform — all transport for jobs received via the platform must be paid through the platform;</li>
          <li>not discriminate against customers on the basis of race, religion, gender, disability, sexual orientation or any other protected characteristic.</li>
        </ul>
      </Section>

      <Section id="reporting" title="8. Reporting breaches" testidPrefix={PREFIX}>
        <p>
          If you see content or behaviour on the Service that breaches this AUP — particularly
          anything involving safety, fraud or harassment — please report it immediately to{" "}
          <a href="mailto:customers@transportanything247.com" className="text-orange-600 underline">customers@transportanything247.com</a>{" "}
          with the booking ID or user reference. We aim to respond within one working day.
        </p>
      </Section>

      <Section id="enforcement" title="9. Enforcement" testidPrefix={PREFIX}>
        <p>
          We use a combination of automated checks and human review to enforce this AUP. Depending on
          severity we may issue a warning, suspend the relevant feature, suspend or close the account,
          withhold or claw back payouts (for drivers), or refer the matter to authorities.
        </p>
        <p>
          We will give you reasonable notice of any decision and an opportunity to respond except
          where immediate action is necessary to protect another user or the integrity of the Service.
        </p>
      </Section>

      <Section id="changes" title="10. Changes to this AUP" testidPrefix={PREFIX}>
        <p>
          We may update this Policy as the platform and the legal landscape evolve. The "Last updated"
          date above shows when it was last revised; material changes will be notified to active users
          by email.
        </p>
      </Section>
    </LegalLayout>
  );
}
